import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { checkPrivileges, MojoPrivilegesService } from 'mojo-privileges';
import { Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  map,
  mergeMap,
  pluck,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { companiesGet } from '@collections/companies';
import { jobFilterSetInitialState } from '@modules/job-page/store/matched-candidates-filter/matched-candidates-filter.actions';
import { AlertActionsUnion, DangerAlert, SuccessAlert } from '@shared/alert';
import { isSingleJobObject, Job } from '@shared/models';
import { Pagination } from '@shared/pagination';
import { JobService } from '@shared/services';
import { TextFormattingService } from '@shared/text-formatting/text-formatting.service';
import { AggregationLoadHiringManager, LoadAggregatedJobsData } from '../aggregations/';
import { CollectionsConfig } from '../collections.abstract.config';
import { UsersList } from '../users';
import {
  EmptyAction,
  JobsActionsUnion,
  JobsActionTypes,
  JobsCreateFail,
  JobsCreateSuccess,
  JobsGetFail,
  JobsGetSuccess,
  JobsLoadCompany,
  JobsStatusUpdateFail,
  JobsStatusUpdateSuccess,
  JobsTeamMembersLoad,
  JobsTeamMembersUpdateFail,
  JobsTeamMembersUpdateSuccess,
  JobsUpdateFail,
  JobsUpdateSuccess,
} from './jobs.actions';

import {
  SharedResources,
  SharedResourcesType,
} from '@core/shared-resources/shared-resources.model';
import { sharedResourcesLoadSuccess, sharedResourcesAddSuccess } from 'mojo-share-resource';
import { convertJobToTeamMembers } from '@modules/jobs-page/utils/utils';
import { getCurrentSharedResource } from '@core/shared-resources/store/shared-resources.selectors';

@Injectable()
export class JobsEffects {
  loadSuccessMatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.LoadSuccess),
      pluck('payload'),
      withLatestFrom(
        this.privilegesService.hasPrivilege(this.collectionsConfig.privileges.hiringManager),
      ),
      mergeMap(([{ content }, isHiringManager]: [Pagination<Job>, boolean]) => {
        const jobsContentIds = content.map(({ id }) => id);
        return !isHiringManager
          ? of(LoadAggregatedJobsData({ jobsIds: jobsContentIds }))
          : !!content &&
              content.map(({ id: jobId }: Job) =>
                AggregationLoadHiringManager({ payload: { params: { jobId } } }),
              );
      }),
    ),
  );

  get$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.Get),
      pluck('payload'),
      distinctUntilChanged<{ id: any }>((prev, next) => prev.id.toString() === next.id.toString()),
      mergeMap((payload) =>
        this.jobService.get(payload).pipe(
          map((job) => ({
            ...job,
            description: this.textFormattingService.removeNonAsciiChars(job.description),
          })),
          concatMap((job: Job) => {
            if (isSingleJobObject(job)) {
              const actionList = [];
              actionList.push(new JobsGetSuccess(job));
              actionList.push(new JobsTeamMembersLoad(job));
              actionList.push(jobFilterSetInitialState({}));
              if (!!job.companyId) {
                actionList.push(companiesGet({ id: job.companyId }));
              }
              return actionList;
            }
            return [new JobsGetSuccess(job)];
          }),
          catchError((error) => of(new JobsGetFail(error))),
        ),
      ),
    ),
  );

  update$: Observable<JobsActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.Update),
      pluck('payload'),
      switchMap(({ id, ...body }: Partial<Job>) =>
        this.jobService.put({ id, body: { id, ...body } }).pipe(
          concatMap((response: Job) =>
            response.companyId
              ? [new JobsLoadCompany(response.companyId), new JobsUpdateSuccess(response)]
              : [new JobsUpdateSuccess(response)],
          ),
          catchError((error) => of(new JobsUpdateFail(error))),
        ),
      ),
    ),
  );

  updateStatus$: Observable<JobsActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.StatusUpdate),
      pluck('payload'),
      switchMap(({ id, ...body }: Partial<Job>) =>
        this.jobService.patch({ id, body: { id, ...body } }).pipe(
          map((response: Job) => new JobsStatusUpdateSuccess(response)),
          catchError((error) => of(new JobsStatusUpdateFail(error))),
        ),
      ),
    ),
  );

  updateSuccess$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.UpdateSuccess, JobsActionTypes.StatusUpdateSuccess),
      map(() => new SuccessAlert('Job details has been updated')),
    ),
  );

  updateFail$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.UpdateFail),
      map(() => new DangerAlert('There was a problem with update')),
    ),
  );

  create$: Observable<JobsActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.Create),
      pluck('payload'),
      switchMap((body) =>
        this.jobService.post({ body }).pipe(
          map((response: Job) => new JobsCreateSuccess(response)),
          catchError((error) => of(new JobsCreateFail(error))),
        ),
      ),
    ),
  );

  createSuccess$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.CreateSuccess),
      map(() => new SuccessAlert('Job has been created')),
    ),
  );

  createFail$: Observable<AlertActionsUnion> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.CreateFail),
      map(() => new DangerAlert('There was a problem creating job')),
    ),
  );

  saveSuccessRedirect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(JobsActionTypes.CreateSuccess, JobsActionTypes.UpdateSuccess),
        pluck('payload'),
        tap((job: Job) => this.router.navigate([`/jobs/job/${job.id}/description`])),
      ),
    { dispatch: false },
  );

  getTeamMembers$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.TeamMembersLoad),
      pluck('payload'),
      withLatestFrom(this.store.select(getCurrentSharedResource)),
      checkPrivileges(
        [this.collectionsConfig.privileges.job.teamMembers.view],
        this.privilegesService.userPrivileges$,
      ),
      concatMap(([job, currentSharedResource]: [Job, SharedResources]) => {
        const userIds = [
          ...job.regularRecruiters.map((user) => user.id),
          ...job.recruitingManagers.map((user) => user.id),
          ...job.hiringManagers.map((user) => user.id),
          ...job.coordinators.map((user) => user.id),
        ];
        const actions = [];
        if (
          currentSharedResource.resourceType === SharedResourcesType.job &&
          +job.id === currentSharedResource.resourceId
        ) {
          const teamMembers = convertJobToTeamMembers(job);
          actions.push(
            sharedResourcesLoadSuccess({
              resourceId: job.id,
              resourceType: SharedResourcesType.job,
              teamMembers,
            }),
          );
        }
        return [...actions, new UsersList({ userIds })];
      }),
    ),
  );

  updateTeamMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.TeamMembersUpdate),
      pluck('payload'),
      switchMap(({ id, members }) =>
        this.jobService.updateTeamMembers(id, members).pipe(
          map((job: Job) => new JobsTeamMembersUpdateSuccess(job)),
          catchError((error) => of(new JobsTeamMembersUpdateFail(error))),
        ),
      ),
    ),
  );

  updateTeamMembersSuccess$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsActionTypes.TeamMembersUpdateSuccess),
      pluck('payload'),
      concatMap((job: Job) => [
        new SuccessAlert('Your changes have been saved'),
        new JobsTeamMembersLoad(job),
      ]),
    ),
  );

  updateSharedTeamMembersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharedResourcesAddSuccess),
      withLatestFrom(this.store.select(getCurrentSharedResource)),
      map(([{ payload, resourceType }, currentResourceState]) => {
        if (currentResourceState.resourceType === SharedResourcesType.job) {
          return new JobsTeamMembersLoad(payload);
        }
        return new EmptyAction();
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private jobService: JobService,
    private privilegesService: MojoPrivilegesService,
    private collectionsConfig: CollectionsConfig,
    private textFormattingService: TextFormattingService,
    private router: Router,
    private store: Store,
  ) {}
}
